/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  useState,
  useRef,
  useEffect,
  CSSProperties,
  useContext,
  ChangeEventHandler,
} from 'react';
import { Input } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import debounce from 'lodash/debounce';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { SearchContext } from '~src/contexts/SearchContext';
import { AdvancedSearchPopOver } from './AdvancedSearchPopOver';
import { appLog } from '~src/utils/log';

interface SearchBarProps {
  mobile?: boolean;
  publicPage?: boolean;
}

// Debounce delay before propagating the query to SearchContext.
// This avoids re-filtering large lists (e.g. 7000+ item tables) on every keystroke.
const SEARCH_DEBOUNCE_MS = 300;

export const SearchBar = ({ mobile, publicPage = false }: SearchBarProps) => {
  const searchContext = useContext(SearchContext);
  const intl = useIntl();
  const style = useStyles(styleFn);
  const [clearInput, setClearInput] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  // Local, immediate value so the input feels responsive while the
  // heavier context update (which triggers list filtering) is debounced.
  const [inputValue, setInputValue] = useState(searchContext.query);

  const isOnMovements = window.location.pathname.includes('/movements');

  const debouncedSearchHandler = useRef(
    debounce((value: string) => {
      searchContext.searchHandler(value);
    }, SEARCH_DEBOUNCE_MS)
  ).current;

  // Cancel any pending debounced call on unmount
  useEffect(() => {
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [debouncedSearchHandler]);

  // Keep local value in sync if the query is reset/changed externally
  useEffect(() => {
    setInputValue(searchContext.query);
  }, [searchContext.query]);

  const onDefaultSearchChange: ChangeEventHandler<HTMLInputElement> = ({
    target,
  }) => {
    setClearInput(false);
    appLog('target => ', target);
    setInputValue(target.value);
    debouncedSearchHandler(target.value);
  };

  if (!mobile) {
    return (
      <div css={style.component}>
        <div
          css={!publicPage ? style.containerAdvanced : style.container}
          key="searchBarContainer">
          <Input
            disabled={isOnMovements}
            prefix={
              <FontAwesomeIcon
                icon={['far', 'search']}
                style={style.icons as CSSProperties}
              />
            }
            css={!publicPage ? style.inputFieldAdvanced : style.inputField}
            placeholder={intl.formatMessage({ id: 'search' })}
            allowClear
            onChange={onDefaultSearchChange}
            onPressEnter={() => {
              setIsPopoverVisible(true);
            }}
            className={`${
              window.sessionStorage.getItem('appState') === 'superUser' &&
              'su-mode-input'
            }`}
            value={clearInput ? '' : inputValue}
          />{' '}
          {!publicPage && (
            <AdvancedSearchPopOver
              isPopoverVisible={isPopoverVisible}
              setIsPopoverVisible={setIsPopoverVisible}
            />
          )}
        </div>
      </div>
    );
  } else {
    return (
      <Input
        css={[style.inputField, { height: 50, width: '100%' }]}
        placeholder={intl.formatMessage({ id: 'search' })}
        allowClear
        onChange={onDefaultSearchChange}
        // onPressEnter={() => {
        //   setIsPopoverVisible(true);
        // }}
        value={clearInput ? '' : inputValue}
      />
    );
  }
};

const styleFn: ThemedStyleFn = (theme) => ({
  component: {
    width: theme.aboveWidth[1],
    minWidth: 154,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.colors.lightGray}`,
    borderRadius: '2px',
    margin: '0 5px',
    padding: '0',
    // width: '70%',
    height: '32px',
    ':focus': {
      border: `1px solid ${theme.colors.primary}`,
    },
  },
  containerAdvanced: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${theme.colors.lightGray}`,
    borderRadius: '2px',
    margin: '0 5px',
    padding: '0',
    // width: '70%',
    height: '32px',
    ':focus': {
      border: `1px solid ${theme.colors.primary}`,
    },
    backgroundColor: `${theme.colors.gray}`,
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  advancedSearchIcon: {
    marginLeft: 'auto',
  },
  inputField: {
    border: 'none!important',
    // margin: '0 5px',
    height: '100%',
    width: '100%',
    color: `${theme.colors.text.primary}`,

    '::placeholder': {
      color: `${theme.colors.text.secondary}`,
    },

    ':focus': {
      outline: 'none',
    },
  },
  inputFieldAdvanced: {
    border: 'none!important',
    // margin: '0 5px',
    height: '100%',
    width: '85%',
    color: `${theme.colors.text.primary}`,

    '::placeholder': {
      color: `${theme.colors.text.secondary}`,
    },

    ':focus': {
      outline: 'none',
    },
  },
});

// export default injectIntl(SearchBar);
